import type { PipelineNodeRelationship } from '../hooks/usePipelineNodeRelationships';

export interface PipelineNodeField {
  id?: string;
  name?: string;
  label?: string;
  type?: string;
  description?: string;
  relationship_id?: string;
  map_options?: Array<{
    id?: string;
    source_node_id?: string;
    attribute_key?: string;
    attribute_id?: string;
  }>;
  part_of_composite_key?: boolean;
  transformer?: string;
  custom_sql?: string;
}

export interface DeleteFieldResult {
  fieldsToRemove: PipelineNodeField[];
  relationshipIdToDelete: string | null;
  relationshipToUpdate: Partial<PipelineNodeRelationship> | null;
}

/**
 * Determines what should happen when deleting a field.
 *
 * - If deleting a FOREIGN_KEY field: returns the field and all associated DENORMALIZED fields,
 *   plus the relationship ID to delete
 * - If deleting a DENORMALIZED field: returns just the field, plus an updated relationship
 *   with the denormalized_fields entry removed
 * - If deleting any other field: returns just the field
 */
export function getDeleteFieldResult(
  fieldToDelete: PipelineNodeField,
  allFields: PipelineNodeField[],
  getRelationship: ((relationshipId: string) => Partial<PipelineNodeRelationship> | undefined) | undefined,
  relationshipsToSave: Map<string, Partial<PipelineNodeRelationship>>
): DeleteFieldResult {
  let fieldsToRemove: PipelineNodeField[] = [fieldToDelete];
  let relationshipIdToDelete: string | null = null;
  let relationshipToUpdate: Partial<PipelineNodeRelationship> | null = null;

  // If this is a FOREIGN_KEY field, find all DENORMALIZED fields that use the same relationship
  if (fieldToDelete.type === 'FOREIGN_KEY' && fieldToDelete.relationship_id) {
    relationshipIdToDelete = fieldToDelete.relationship_id;

    // Find all DENORMALIZED fields that use this relationship
    const denormalizedFieldsToDelete = allFields.filter(
      (f) =>
        f.type === 'DENORMALIZED' &&
        f.relationship_id === relationshipIdToDelete
    );

    fieldsToRemove = [fieldToDelete, ...denormalizedFieldsToDelete];
  } else if (
    // If this is a DENORMALIZED field with a relationship_id,
    // we should also clean up the relationship's denormalized_fields
    fieldToDelete.type === 'DENORMALIZED' &&
    fieldToDelete.relationship_id &&
    getRelationship
  ) {
    const relationshipId = fieldToDelete.relationship_id;
    const relationship = relationshipsToSave.has(relationshipId)
      ? relationshipsToSave.get(relationshipId)!
      : getRelationship(relationshipId);

    if (relationship) {
      // Remove denormalized_fields entries for this field
      const updatedDenormalizedFields = (relationship.denormalized_fields || []).filter(
        (df) => df.output_field_name !== (fieldToDelete.name || fieldToDelete.label)
      );

      relationshipToUpdate = {
        ...relationship,
        denormalized_fields: updatedDenormalizedFields,
      };
    }
  }

  return {
    fieldsToRemove,
    relationshipIdToDelete,
    relationshipToUpdate,
  };
}

/**
 * Updates the denormalized_fields on a relationship when configuring a DENORMALIZED field.
 * Returns the updated relationship.
 */
export function updateDenormalizedFieldOnRelationship(
  relationship: Partial<PipelineNodeRelationship>,
  outputFieldName: string,
  parentFieldId: string | null
): Partial<PipelineNodeRelationship> {
  const existingFields = relationship.denormalized_fields || [];

  // Remove all existing entries for this output_field_name
  const otherFields = existingFields.filter(
    (df) => df.output_field_name !== outputFieldName
  );

  // Add one entry for the selected parent field (if a value is selected)
  const updatedDenormalizedFields = parentFieldId
    ? [
        ...otherFields,
        {
          parent_field_id: parentFieldId,
          output_field_name: outputFieldName,
        },
      ]
    : otherFields;

  return {
    ...relationship,
    denormalized_fields: updatedDenormalizedFields,
  };
}

/**
 * Removes a denormalized field entry from a relationship.
 * Used when switching a DENORMALIZED field to a different relationship.
 */
export function removeDenormalizedFieldFromRelationship(
  relationship: Partial<PipelineNodeRelationship>,
  outputFieldName: string
): Partial<PipelineNodeRelationship> {
  return {
    ...relationship,
    denormalized_fields: (relationship.denormalized_fields || []).filter(
      (df) => df.output_field_name !== outputFieldName
    ),
  };
}

/**
 * Removes fields from the fields array based on a set of field IDs to remove.
 */
export function removeFieldsFromList(
  fields: PipelineNodeField[],
  fieldsToRemove: PipelineNodeField[]
): PipelineNodeField[] {
  const fieldIdsToRemove = new Set(
    fieldsToRemove.map((f) => f.id || f.name).filter(Boolean)
  );

  return fields.filter(
    (f) => !fieldIdsToRemove.has(f.id || f.name || '')
  );
}

/**
 * Updates a field's relationship_id in the fields array.
 */
export function updateFieldRelationshipId(
  fields: PipelineNodeField[],
  fieldIdOrName: string,
  newRelationshipId: string | undefined
): PipelineNodeField[] {
  return fields.map((f) => {
    if (f.id === fieldIdOrName || f.name === fieldIdOrName) {
      return {
        ...f,
        relationship_id: newRelationshipId,
      };
    }
    return f;
  });
}

/**
 * Prepares save operations for relationships based on pending changes.
 * Returns arrays of relationships to save and delete.
 */
export interface RelationshipSaveOperations {
  relationshipsToSave: Array<Partial<PipelineNodeRelationship>>;
  relationshipIdsToDelete: string[];
}

export function prepareRelationshipSaveOperations(
  relationshipsToSaveMap: Map<string, Partial<PipelineNodeRelationship>>,
  relationshipsToDeleteSet: Set<string>
): RelationshipSaveOperations {
  // Filter out any relationships that are being deleted from the save list
  const relationshipsToSave = Array.from(relationshipsToSaveMap.entries())
    .filter(([relId]) => !relationshipsToDeleteSet.has(relId))
    .map(([relId, relData]) => ({ ...relData, id: relId }));

  const relationshipIdsToDelete = Array.from(relationshipsToDeleteSet);

  return {
    relationshipsToSave,
    relationshipIdsToDelete,
  };
}

/**
 * Prefix used for pending (unsaved) relationship IDs.
 * Pending relationships get a synthetic ID like "pending:fieldKey"
 * so they can be referenced by DENORMALIZED fields before being saved.
 */
export const PENDING_RELATIONSHIP_PREFIX = 'pending:';

/**
 * Checks if a relationship ID is a pending (synthetic) ID.
 */
export function isPendingRelationshipId(relationshipId: string | undefined): boolean {
  return !!relationshipId && relationshipId.startsWith(PENDING_RELATIONSHIP_PREFIX);
}

/**
 * Extracts the field key from a pending relationship ID.
 * Returns null if not a pending ID.
 */
export function getPendingFieldKey(relationshipId: string | undefined): string | null {
  if (!isPendingRelationshipId(relationshipId)) {
    return null;
  }
  return relationshipId!.replace(PENDING_RELATIONSHIP_PREFIX, '');
}

/**
 * Creates a pending relationship ID from a field key.
 */
export function createPendingRelationshipId(fieldKey: string): string {
  return `${PENDING_RELATIONSHIP_PREFIX}${fieldKey}`;
}

/**
 * Updates fields with real relationship IDs after pending relationships have been saved.
 * - Updates FOREIGN_KEY fields to have the real relationship_id
 * - Updates DENORMALIZED fields that reference pending relationships to use real IDs
 * - Clears relationship_id for DENORMALIZED fields whose pending relationship wasn't saved
 */
export function updateFieldsWithRealRelationshipIds(
  fields: PipelineNodeField[],
  pendingToRealIdMap: Map<string, string>
): PipelineNodeField[] {
  return fields.map((field) => {
    // Update FOREIGN_KEY fields
    if (field.type === 'FOREIGN_KEY') {
      const fieldKey = field.id || field.name || '';
      if (pendingToRealIdMap.has(fieldKey)) {
        return {
          ...field,
          relationship_id: pendingToRealIdMap.get(fieldKey),
        };
      }
    }

    // Update DENORMALIZED fields that reference pending relationships
    if (field.type === 'DENORMALIZED' && isPendingRelationshipId(field.relationship_id)) {
      const pendingKey = getPendingFieldKey(field.relationship_id);
      if (pendingKey && pendingToRealIdMap.has(pendingKey)) {
        return {
          ...field,
          relationship_id: pendingToRealIdMap.get(pendingKey),
        };
      } else {
        // Pending relationship not found - clear the invalid relationship_id
        return {
          ...field,
          relationship_id: undefined,
        };
      }
    }

    return field;
  });
}

/**
 * Prepares a pending relationship for saving.
 * Ensures id is undefined (not null) so it's treated as a new relationship.
 */
export function preparePendingRelationshipForSave(
  pendingRelationship: Partial<PipelineNodeRelationship>,
  childNodeId: string,
  childForeignKeyName: string
): Partial<PipelineNodeRelationship> {
  return {
    ...pendingRelationship,
    child_node_id: childNodeId,
    child_foreign_key_name: childForeignKeyName,
    // Ensure id is undefined for new relationships (don't send null or the pending: prefix)
    id: undefined,
  };
}
